import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { EditableSelect } from "@/blocks/Editable/EditableSelect";
import { EditableTextarea } from "@/blocks/Editable/EditableTextarea";
import {
  PlaybookResultFragment,
  TopicResultFragment,
} from "@/graphql/generated/graphql";
import { useGetLicenses } from "@/hooks/useGetLicenses/useGetLicenses";
import { useSavePlaybookResult } from "@/hooks/useSavePlaybookResult/useSavePlaybookResult";

export type PlaybookTextVariant =
  | "bodyRegularS"
  | "bodyMediumS"
  | "bodySemiboldS"
  | "bodyRegularM"
  | "bodySemiboldM";

const variantClassName: Record<PlaybookTextVariant, string> = {
  bodyRegularS: "text-xs font-normal",
  bodyMediumS: "text-xs font-medium",
  bodySemiboldS: "text-xs font-semibold",
  bodyRegularM: "text-sm font-normal",
  bodySemiboldM: "text-sm font-semibold",
};

function getVariantClassName(variant: PlaybookTextVariant): string {
  return variantClassName[variant];
}

type PlaybookResultFormFieldProps = {
  topicResult: TopicResultFragment;
  onUpdate: (newValue: string | boolean) => void;
  readOnly?: boolean;
  variants: {
    title: PlaybookTextVariant;
    field: PlaybookTextVariant;
  };
};

export function PlaybookResultFormField({
  topicResult,
  onUpdate,
  readOnly = false,
  variants,
}: PlaybookResultFormFieldProps) {
  const {
    name,
    content: { response },
  } = topicResult;

  const { t } = useTranslation();

  const stringValue = useMemo(() => {
    if (response?.__typename === "StringValue") {
      return response.stringValue;
    }
    if (response?.__typename === "BooleanValue") {
      return response.booleanValue
        ? t("PlaybookResults.Yes")
        : t("PlaybookResults.No");
    }
    return undefined;
  }, [response, t]);

  return (
    <div className="flex flex-col gap-1">
      <span
        className={`text-accent-foreground ${getVariantClassName(variants.title)}`}
      >
        {name}
      </span>

      {readOnly ? (
        <span
          className={`whitespace-pre-line text-foreground ${getVariantClassName(
            variants.field
          )}`}
        >
          {stringValue}
        </span>
      ) : (
        <>
          {response.__typename === "StringValue" && (
            <EditableTextarea
              defaultValue={response?.stringValue ?? undefined}
              onSubmit={(newValue) => onUpdate(newValue)}
              className={getVariantClassName(variants.field)}
            />
          )}

          {response.__typename === "BooleanValue" && (
            <EditableSelect<boolean>
              defaultValue={response?.booleanValue ?? undefined}
              placeholder={t("call_context.summary_empty_message")}
              onChange={onUpdate}
              className={getVariantClassName(variants.field)}
              options={[
                {
                  label: t("Yes"),
                  value: true,
                },
                {
                  label: t("No"),
                  value: false,
                },
              ]}
            />
          )}
        </>
      )}
    </div>
  );
}

export type PlaybookResultsFormProps = {
  playbookResult: Omit<
    PlaybookResultFragment,
    "callUuid" | "language" | "name"
  >;
  variants?: {
    title: PlaybookTextVariant;
    field: PlaybookTextVariant;
  };
};

export function PlaybookResultsForm({
  playbookResult,
  variants = {
    title: "bodySemiboldS",
    field: "bodyRegularS",
  },
}: PlaybookResultsFormProps) {
  const { savePlaybookResult } = useSavePlaybookResult();
  const { hasLicense } = useGetLicenses();
  const canUpdatePlaybook = hasLicense("ai_assist_pro");

  const handleUpdate = async (
    topicResult: TopicResultFragment,
    newValue: string | boolean
  ) => {
    /* v8 ignore next 3 -- defensive: editable controls are not rendered when unlicensed */
    if (!canUpdatePlaybook) {
      return;
    }

    // Get current value based on response type
    let currentValue: string | boolean | undefined;
    if (topicResult.content.response.__typename === "StringValue") {
      currentValue = topicResult.content.response.stringValue;
    } else if (topicResult.content.response.__typename === "BooleanValue") {
      currentValue = topicResult.content.response.booleanValue;
    }

    // Do nothing if the value hasn't changed
    if (currentValue === newValue) {
      return;
    }

    // Prepare the topic result input based on the response type
    const topicResultInput = {
      id: topicResult.id,
      ...(typeof newValue === "string"
        ? { responseString: newValue }
        : { responseBoolean: newValue }),
    };

    await savePlaybookResult({
      input: {
        id: playbookResult.id,
        topicResults: [topicResultInput],
      },
    });
  };

  return (
    <div className="flex w-full flex-col gap-2">
      {playbookResult.topicResults.map((topicResult) => {
        return (
          <PlaybookResultFormField
            key={topicResult.id}
            topicResult={topicResult}
            onUpdate={(newValue) => handleUpdate(topicResult, newValue)}
            readOnly={!canUpdatePlaybook}
            variants={variants}
          />
        );
      })}
    </div>
  );
}
